/**
 * useFriendsInvitations hook
 * Manages invitation-related state and actions: sending invitations, managing contacts, manual emails
 */

import { useCallback } from 'react'
import { buildEndpointUrl } from '../../../constants'
import { logger } from '../../helpers/logger.helper'
import { EMAIL_REGEX } from '../../helpers/sanitizeInput.helper'
import type { Friend } from '../types/friends.types'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { SnackbarVariant } from '../../shared/stores/snackbarStore'

interface UseFriendsInvitationsProps {
  setShowInviteModal: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedContacts: React.Dispatch<React.SetStateAction<Set<string>>>
  setInviteMessage: React.Dispatch<React.SetStateAction<string>>
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  setManualEmail: React.Dispatch<React.SetStateAction<string>>
  setManualEmails: React.Dispatch<React.SetStateAction<Set<string>>>
  setInviteTab: React.Dispatch<React.SetStateAction<'manual' | 'link'>>
  setSendingInvites: React.Dispatch<React.SetStateAction<boolean>>
  setSnackbar: React.Dispatch<React.SetStateAction<{ message: string; variant: SnackbarVariant } | null>>
  refreshFriends: () => Promise<void>
  selectedContacts: Set<string>
  manualEmail: string
  manualEmails: Set<string>
  inviteMessage: string
  friends: Friend[]
  currentUserId: string
  getUserDisplayName: () => string | null
  deduplicateFriendsByEmail: (friends: Friend[]) => Friend[]
  apiClient: <T = any>(endpoint: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>
  inviteTab: 'manual' | 'link'
  onInvitesSent?: (method: 'manual' | 'link', count: number) => void
}

interface UseFriendsInvitationsReturn {
  handleCloseModal: () => void
  toggleContactSelection: (email: string) => void
  addManualEmail: () => void
  removeManualEmail: (email: string) => void
  handleSendInvitations: () => Promise<void>
}

export const useFriendsInvitations = ({
  setShowInviteModal,
  setSelectedContacts,
  setInviteMessage,
  setSearchQuery,
  setManualEmail,
  setManualEmails,
  setInviteTab,
  setSendingInvites,
  setSnackbar,
  refreshFriends,
  selectedContacts,
  manualEmail,
  manualEmails,
  inviteMessage,
  friends,
  currentUserId,
  getUserDisplayName,
  deduplicateFriendsByEmail,
  apiClient,
  inviteTab,
  onInvitesSent,
}: UseFriendsInvitationsProps): UseFriendsInvitationsReturn => {
  const handleCloseModal = useCallback(() => {
    setShowInviteModal(false)
    setSelectedContacts(new Set())
    setInviteMessage('')
    setSearchQuery('')
    setManualEmail('')
    setManualEmails(new Set())
    setInviteTab('manual')
  }, [
    setShowInviteModal,
    setSelectedContacts,
    setInviteMessage,
    setSearchQuery,
    setManualEmail,
    setManualEmails,
    setInviteTab,
  ])

  const toggleContactSelection = useCallback(
    (email: string) => {
      const newSelected = new Set(selectedContacts)
      if (newSelected.has(email)) {
        newSelected.delete(email)
      } else {
        newSelected.add(email)
      }
      setSelectedContacts(newSelected)
    },
    [selectedContacts, setSelectedContacts]
  )

  const addManualEmail = useCallback(() => {
    const email = manualEmail.trim().toLowerCase()
    if (!email) return

    if (!EMAIL_REGEX.test(email)) {
      logger.error('[useFriendsInvitations] Invalid email format', { email });
      setSnackbar({ message: 'Please enter a valid email address', variant: 'error' });
      return
    }

    const existingFriend = friends.find(
      (friend) => friend.email?.toLowerCase() === email.toLowerCase()
    )

    if (existingFriend) {
      setManualEmail('')
      handleCloseModal()

      setTimeout(() => {
        const message =
          existingFriend.friendshipStatus === 'pending'
            ? `${email} has already been invited`
            : `${email} is already your friend`
        setSnackbar({ message, variant: 'info' })
      }, 100)
      return
    }

    const newManualEmails = new Set(manualEmails)
    newManualEmails.add(email)
    setManualEmails(newManualEmails)

    const newSelected = new Set(selectedContacts)
    newSelected.add(email)
    setSelectedContacts(newSelected)

    setManualEmail('')
  }, [
    manualEmail,
    friends,
    handleCloseModal,
    setSnackbar,
    manualEmails,
    setManualEmails,
    selectedContacts,
    setSelectedContacts,
    setManualEmail,
  ])

  const removeManualEmail = useCallback(
    (email: string) => {
      const newManualEmails = new Set(manualEmails)
      newManualEmails.delete(email)
      setManualEmails(newManualEmails)

      const newSelected = new Set(selectedContacts)
      newSelected.delete(email)
      setSelectedContacts(newSelected)
    },
    [manualEmails, setManualEmails, selectedContacts, setSelectedContacts]
  )

  const handleSendInvitations = useCallback(async () => {
    if (selectedContacts.size === 0) {
      logger.error('[useFriendsInvitations] No contacts selected');
      setSnackbar({ message: 'Please select at least one contact to invite.', variant: 'error' });
      return
    }

    const emails = Array.from(selectedContacts)
    const alreadyFriends: Array<{ email: string; status: 'pending' | 'accepted' }> = []

    emails.forEach((email) => {
      const existingFriend = friends.find(
        (friend) => friend.email?.toLowerCase() === email.toLowerCase()
      )
      if (existingFriend) {
        alreadyFriends.push({ email, status: existingFriend.friendshipStatus || 'accepted' })
      }
    })

    if (alreadyFriends.length > 0) {
      const pendingCount = alreadyFriends.filter((f) => f.status === 'pending').length
      const acceptedCount = alreadyFriends.filter((f) => f.status === 'accepted').length

      let message = ''
      if (alreadyFriends.length === 1) {
        const friend = alreadyFriends[0]
        message = friend.status === 'pending'
          ? `${friend.email} has already been invited`
          : `${friend.email} is already your friend`
      } else {
        const parts: string[] = []
        if (pendingCount > 0) parts.push(`${pendingCount} already invited`)
        if (acceptedCount > 0) parts.push(`${acceptedCount} already friends`)
        message = `${alreadyFriends.length} emails: ${parts.join(', ')}`
      }

      setSnackbar({ message, variant: 'info' })

      const newSelected = new Set(selectedContacts)
      alreadyFriends.forEach((f) => newSelected.delete(f.email))
      setSelectedContacts(newSelected)

      if (newSelected.size === 0) {
        handleCloseModal()
        return
      }
    }

    setSendingInvites(true)
    try {
      const emailsToInvite = Array.from(selectedContacts).filter(
        (email) => !alreadyFriends.some((f) => f.email === email)
      )
      const userDisplayName = getUserDisplayName()

      const response = await apiClient<{
        success: boolean
        data?: { sent?: number; invitations?: Array<{ token: string }>; errors?: Array<{ error?: string }> }
        error?: string
      }>(buildEndpointUrl('invitations', 'send'), {
        method: 'POST',
        data: {
          emails: emailsToInvite,
          message: inviteMessage.trim() || undefined,
          inviterName: userDisplayName,
        },
        headers: { 'Content-Type': 'application/json' },
      })

      const data = response.data
      if (data?.success) {
        setShowInviteModal(false)
        setSelectedContacts(new Set())
        setInviteMessage('')
        setSearchQuery('')
        setManualEmail('')
        setManualEmails(new Set())

        const totalInvitations = data.data?.sent || 0

        setSnackbar({
          message: `${totalInvitations} invitation(s) sent successfully!`,
          variant: 'success',
        })
        onInvitesSent?.(inviteTab, totalInvitations)

        await refreshFriends()
      }
    } catch (error) {
      logger.error('[useFriendsInvitations] Failed to send invitations', { error })
      setSnackbar({ message: 'Failed to send invitations. Please try again.', variant: 'error' })
    } finally {
      setSendingInvites(false)
    }
  }, [
    selectedContacts,
    friends,
    setSnackbar,
    setSelectedContacts,
    handleCloseModal,
    setSendingInvites,
    currentUserId,
    getUserDisplayName,
    inviteMessage,
    setShowInviteModal,
    setInviteMessage,
    setSearchQuery,
    setManualEmail,
    setManualEmails,
    apiClient,
    deduplicateFriendsByEmail,
    refreshFriends,
    inviteTab,
    onInvitesSent,
  ])

  return {
    handleCloseModal,
    toggleContactSelection,
    addManualEmail,
    removeManualEmail,
    handleSendInvitations,
  }
}
